import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import FinanceV2Layout from "./FinanceV2Layout";
import { NoteState } from "../../context/notes/NoteState";
import FinanceV2Services from "../../UserServices/FinanceV2Services";
import "./FinanceV2.css";

export default function FinanceV2Mappings() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const [tab, setTab] = useState("payments");
  const [accounts, setAccounts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMappings, setPaymentMappings] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorForm, setVendorForm] = useState({
    name: "",
    contact: "",
    default_expense_account_id: "",
    uses_accounts_payable: false,
  });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [accRes, mapRes, optRes, venRes] = await Promise.all([
        FinanceV2Services.getAccountsList(branch_id),
        FinanceV2Services.getMappings(branch_id),
        FinanceV2Services.getWizardOptions(branch_id),
        FinanceV2Services.getVendors(branch_id),
      ]);
      const acc = accRes.data?.data || [];
      setAccounts(acc);
      const maps = mapRes.data?.data || [];
      const pms = optRes.data?.data?.payment_methods || [];
      setPaymentMethods(pms);
      setVendors(venRes.data?.data || []);

      const byKey = {};
      maps
        .filter((m) => m.source_type === "payment_method")
        .forEach((m) => {
          byKey[m.source_key] = m.account_id;
        });

      setPaymentMappings(
        pms.map((pm) => ({
          source_module: "sales",
          source_type: "payment_method",
          source_key: pm.id,
          source_label: pm.name,
          account_id: byKey[pm.id] ? String(byKey[pm.id]) : "",
        }))
      );
    } catch {
      toast.error("Could not load mappings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branch_id]);

  const assetAccounts = accounts.filter((a) =>
    ["asset", "liability"].includes(a.account_type?.code)
  );
  const expenseAccounts = accounts.filter((a) => a.account_type?.code === "expense");

  const seedPayments = async () => {
    setLoading(true);
    try {
      const res = await FinanceV2Services.seedPaymentMethodMappings(branch_id);
      toast.success(`Mapped ${res.data?.seeded ?? 0} payment methods (Cash, Card, Online, etc.)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Seed failed");
    } finally {
      setLoading(false);
    }
  };

  const savePayments = async () => {
    const mappings = paymentMappings
      .filter((m) => m.account_id)
      .map((m) => ({
        ...m,
        source_key: parseInt(m.source_key, 10),
        account_id: parseInt(m.account_id, 10),
      }));
    if (!mappings.length) {
      toast.info("Select an account for at least one payment method");
      return;
    }
    try {
      await FinanceV2Services.saveMappings({ mappings, branch_id });
      toast.success("Payment mappings saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Save failed");
    }
  };

  const importVendors = async () => {
    try {
      const res = await FinanceV2Services.importVendorsFromExpenses(branch_id);
      const d = res.data?.data || {};
      toast.success(`Vendors: ${d.created} new, ${d.total} total`);
      load();
    } catch {
      toast.error("Import vendors failed");
    }
  };

  const saveVendor = async (e) => {
    e.preventDefault();
    if (!vendorForm.name.trim()) return;
    try {
      await FinanceV2Services.createVendor({
        ...vendorForm,
        branch_id,
        default_expense_account_id: vendorForm.default_expense_account_id || null,
        uses_accounts_payable: vendorForm.uses_accounts_payable,
      });
      toast.success("Vendor saved");
      setVendorForm({ name: "", contact: "", default_expense_account_id: "", uses_accounts_payable: false });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save vendor");
    }
  };

  return (
    <FinanceV2Layout>
      <div className="fv2-dashboard">
        <header className="fv2-page-header mb-2">
          <div>
            <h1 className="fv2-page-title">Mappings & vendors</h1>
            <p className="fv2-page-sub mb-0">
              Map payment methods (releated_thing IDs 31 Cash, 32 Cheque, 33 Card, 35 Online…) and manage vendors for expenses.
            </p>
          </div>
          <Link to="/finance-v2/dashboard" className="btn btn-sm btn-outline-secondary">
            Back
          </Link>
        </header>

        <div className="btn-group mb-3">
          <button
            type="button"
            className={`btn btn-sm ${tab === "payments" ? "btn-success" : "btn-outline-secondary"}`}
            onClick={() => setTab("payments")}
          >
            Payment methods
          </button>
          <button
            type="button"
            className={`btn btn-sm ${tab === "vendors" ? "btn-success" : "btn-outline-secondary"}`}
            onClick={() => setTab("vendors")}
          >
            Vendors
          </button>
        </div>

        {loading ? (
          <p>Loading…</p>
        ) : tab === "payments" ? (
          <div className="fv2-card">
            <div className="d-flex flex-wrap gap-2 mb-3">
              <button type="button" className="btn btn-sm btn-primary" onClick={seedPayments}>
                Auto-map all (recommended)
              </button>
              <button type="button" className="btn btn-sm btn-success" onClick={savePayments}>
                Save changes
              </button>
            </div>
            <p className="small text-muted">
              These IDs come from <code>releated_thing</code> where type = PaymentMethod. Sales and payroll use them to pick Cash (1000), Bank (1010), or A/R (1200).
            </p>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Method</th>
                  <th>Deposit account</th>
                </tr>
              </thead>
              <tbody>
                {paymentMappings.map((m, idx) => (
                  <tr key={m.source_key}>
                    <td>{m.source_key}</td>
                    <td>{m.source_label}</td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={m.account_id}
                        onChange={(e) => {
                          const next = [...paymentMappings];
                          next[idx].account_id = e.target.value;
                          setPaymentMappings(next);
                        }}
                      >
                        <option value="">— Select —</option>
                        {assetAccounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} {a.name}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
                {!paymentMappings.length && (
                  <tr>
                    <td colSpan={3} className="text-muted">
                      No payment methods found. Check releated_thing table.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="row g-3">
            <div className="col-lg-5">
              <div className="fv2-card">
                <h6 className="fw-bold">Add vendor</h6>
                <form onSubmit={saveVendor}>
                  <div className="mb-2">
                    <label className="form-label small">Name</label>
                    <input
                      className="form-control form-control-sm"
                      value={vendorForm.name}
                      onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small">Contact</label>
                    <input
                      className="form-control form-control-sm"
                      value={vendorForm.contact}
                      onChange={(e) => setVendorForm({ ...vendorForm, contact: e.target.value })}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small">Default expense account</label>
                    <select
                      className="form-select form-select-sm"
                      value={vendorForm.default_expense_account_id}
                      onChange={(e) =>
                        setVendorForm({ ...vendorForm, default_expense_account_id: e.target.value })
                      }
                    >
                      <option value="">— Default 5000 —</option>
                      {expenseAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="useAp"
                      checked={vendorForm.uses_accounts_payable}
                      onChange={(e) =>
                        setVendorForm({ ...vendorForm, uses_accounts_payable: e.target.checked })
                      }
                    />
                    <label className="form-check-label small" htmlFor="useAp">
                      Bill to Accounts Payable (2000) instead of cash
                    </label>
                  </div>
                  <button type="submit" className="btn btn-sm btn-success">
                    Save vendor
                  </button>
                </form>
              </div>
            </div>
            <div className="col-lg-7">
              <div className="fv2-card">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold mb-0">Vendors ({vendors.length})</h6>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={importVendors}>
                    Import from legacy expenses
                  </button>
                </div>
                <p className="small text-muted">
                  Legacy expense <code>source</code> field becomes vendor name. Linked on new expense sync and manual entries.
                </p>
                <div className="table-responsive" style={{ maxHeight: 400 }}>
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Expense account</th>
                        <th>A/P</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendors.map((v) => (
                        <tr key={v.id}>
                          <td>{v.name}</td>
                          <td className="small">
                            {v.default_expense_account?.code || v.default_expense_account_id || "5000"}
                          </td>
                          <td>{v.uses_accounts_payable ? "Yes" : "—"}</td>
                        </tr>
                      ))}
                      {!vendors.length && (
                        <tr>
                          <td colSpan={3} className="text-muted">
                            No vendors — import from expenses or add one.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </FinanceV2Layout>
  );
}
